import { ArrowRight } from "lucide-react";
import EventPicker from "./book/EventPicker";
import useBookingForm from "./book/useBookingForm";

function QtyStepper({ qty, onChange }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm font-bold">How many?</span>
      <div className="inline-flex items-center rounded-round border border-[#EFE7D2]">
        <button
          onClick={() => onChange(Math.max(1, qty - 1))}
          className="px-4 py-2"
          data-testid="qty-dec"
        >
          −
        </button>
        <span className="px-4 font-bold" data-testid="qty-val">
          {qty}
        </span>
        <button
          onClick={() => onChange(qty + 1)}
          className="px-4 py-2"
          data-testid="qty-inc"
        >
          +
        </button>
      </div>
    </div>
  );
}

export default function BookPage() {
  const f = useBookingForm();

  return (
    <div className="container-x py-12 md:py-20" data-testid="book-page">
      <p className="eyebrow">Book your place · The Genesis</p>
      <h1 className="heading-xl mt-2">
        Two days. <span className="italic-accent">One island.</span>
      </h1>
      <p className="body-lead mt-3 max-w-xl">
        Pick a day — Discovery (Wed 19 Aug) or Thanksgiving (Thu 20 Aug) —
        tell us who&apos;s coming, and we&apos;ll hold your place. Pricing and
        payment details follow once 2026 ticketing opens.
      </p>

      <div className="mt-10 grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-8">
          <section>
            <h2 className="font-serif text-2xl text-navy mb-4">Pick a day</h2>
            <EventPicker
              events={f.events}
              value={f.eventId}
              onChange={f.setEventId}
            />
          </section>

          <section className="space-y-4">
            <h2 className="font-serif text-2xl text-navy">Your details</h2>
            <div className="grid gap-3">
              <input
                value={f.name}
                onChange={(e) => f.setName(e.target.value)}
                placeholder="Full name"
                className="rounded-xl border border-[#EFE7D2] p-3 bg-cream/40"
                data-testid="input-name"
              />
              <input
                value={f.email}
                onChange={(e) => f.setEmail(e.target.value)}
                placeholder="Email"
                type="email"
                className="rounded-xl border border-[#EFE7D2] p-3 bg-cream/40"
                data-testid="input-email"
              />
              <QtyStepper qty={f.qty} onChange={f.setQty} />
              <p className="text-xs text-grey leading-relaxed mt-1">
                We&apos;ll send your reservation confirmation to this address. No
                payment is taken — pricing &amp; payment follow once 2026
                ticketing opens.
              </p>
            </div>
            {f.error && (
              <p className="text-red text-sm mt-3" data-testid="pay-error">
                {f.error}
              </p>
            )}
            <button
              disabled={!f.canSubmit || f.loading}
              onClick={f.submit}
              className="btn-primary disabled:opacity-50"
              data-testid="pay-btn"
            >
              {f.loading ? "Reserving…" : "Book my place"}{" "}
              <ArrowRight size={16} />
            </button>
          </section>
        </div>

        <aside
          className="card p-6 h-fit md:sticky md:top-24"
          data-testid="order-summary"
        >
          <p className="eyebrow">Your reservation</p>
          <div className="mt-3 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-grey">Day</span>
              <span className="font-semibold text-right max-w-[200px]">
                {f.event
                  ? `${f.event.weekday} ${f.event.month_short} ${f.event.day} · ${f.event.time_range}`
                  : "—"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-grey">Guests</span>
              <span className="font-semibold">{f.qty}</span>
            </div>
          </div>
          <hr className="my-4 border-[#EFE7D2]" />
          <p className="text-xs text-grey leading-relaxed">
            Pricing for 2026 will be announced soon. Book your place now and
            we&apos;ll confirm details by email before tickets open.
          </p>
        </aside>
      </div>
    </div>
  );
}
